import React, { useState, useEffect, useMemo } from "react";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import {
  LuPhone,
  LuMail,
  LuX,
  LuShield,
  LuLock,
  LuArrowRight,
} from "react-icons/lu";

const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const BTN_HOVER = "#162C47";
const OFF_WHITE = "#F1EBEB";
const INPUT_BORDER = "#d0dff0";
const PROFILE_FONT = '"Inter", sans-serif';

interface EditModalProps {
  open: boolean;
  type: "phone" | "email" | "text" | "address";
  value: string;
  onConfirm: (newValue: string) => void;
  onClose: () => void;
}

const maskPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  const last4 = digits.slice(-4);
  const masked = "*".repeat(Math.max(6, digits.length - 4));
  return `+91 ${masked}${last4}`;
};

const maskEmail = (email: string) => {
  if (!email.includes("@")) return email;
  const [local, domain] = email.split("@");
  const visible = local.slice(0, 2);
  return `${visible}${"*".repeat(Math.max(3, local.length - 2))}@${domain}`;
};

const typeConfig = {
  phone: {
    title: "Edit Phone Number",
    subtitle: "Update your phone number linked to your account.",
    currentLabel: "Current Phone Number",
    newLabel: "New Phone Number",
    placeholder: "Enter new phone number",
    icon: LuPhone,
    inputType: "tel" as const,
  },
  email: {
    title: "Edit Email Address",
    subtitle: "Update your email address linked to your account.",
    currentLabel: "Current Email Address",
    newLabel: "New Email Address",
    placeholder: "Enter new email address",
    icon: LuMail,
    inputType: "email" as const,
  },
  text: {
    title: "Edit Details",
    subtitle: "Update your information linked to your account.",
    currentLabel: "Current Value",
    newLabel: "New Value",
    placeholder: "Enter new value",
    icon: LuMail,
    inputType: "text" as const,
  },
  address: {
    title: "Edit Address",
    subtitle: "Update your address linked to your account.",
    currentLabel: "Current Address",
    newLabel: "New Address",
    placeholder: "Enter new address",
    icon: LuMail,
    inputType: "text" as const,
  },
};

const EditModal: React.FC<EditModalProps> = ({
  open,
  type,
  value,
  onConfirm,
  onClose,
}) => {
  const [newValue, setNewValue] = useState("");
  const config = typeConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    if (open) setNewValue("");
  }, [open, value]);

  const displayValue = useMemo(() => {
    if (type === "phone") return maskPhone(value);
    if (type === "email") return maskEmail(value);
    return value;
  }, [type, value]);

  const handleClose = () => {
    setNewValue("");
    onClose();
  };

  const handleConfirm = () => {
    if (type === "phone") {
      if (newValue.trim() && /^\d{10}$/.test(newValue)) {
        onConfirm(newValue);
      } else {
        alert("Please enter a valid 10-digit phone number.");
      }
    } else if (type === "email") {
      if (newValue.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
        onConfirm(newValue);
      } else {
        alert("Please enter a valid email address.");
      }
    } else if (newValue.trim()) {
      onConfirm(newValue);
    } else {
      alert("Please enter a valid value.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-modal-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
          width: 520,
          maxWidth: "100%",
          fontFamily: PROFILE_FONT,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3 }, pb: 2 }}>
          <IconButton
            onClick={handleClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              border: "1px solid #E0E0E0",
              color: DARK_NAVY,
            }}
          >
            <LuX size={18} />
          </IconButton>

          <Typography
            id="edit-modal-title"
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: DARK_NAVY,
              pr: 5,
              lineHeight: 1.3,
            }}
          >
            {config.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: DARK_NAVY_LIGHT,
              mt: 0.75,
              mb: 2.5,
            }}
          >
            {config.subtitle}
          </Typography>

          <Box
            sx={{
              bgcolor: OFF_WHITE,
              borderRadius: "12px",
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Icon size={20} color={BLUE} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: DARK_NAVY_LIGHT,
                  mb: 0.25,
                }}
              >
                {config.currentLabel}
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: DARK_NAVY,
                  wordBreak: "break-all",
                }}
              >
                {displayValue}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "rgba(8, 153, 31, 0.1)",
                color: "#08991F",
                px: 1.25,
                py: 0.5,
                borderRadius: "20px",
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              <LuLock size={12} />
              Verified
            </Box>
          </Box>

          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: DARK_NAVY,
              mb: 1,
            }}
          >
            {config.newLabel}{" "}
            <Box component="span" sx={{ color: "#c0392b" }}>
              *
            </Box>
          </Typography>
          <Box
            component="input"
            type={config.inputType}
            value={newValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewValue(e.target.value)
            }
            placeholder={config.placeholder}
            maxLength={type === "phone" ? 10 : undefined}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              px: 2,
              py: 1.5,
              fontSize: 15,
              fontFamily: PROFILE_FONT,
              color: DARK_NAVY,
              border: `2px solid ${INPUT_BORDER}`,
              borderRadius: "10px",
              outline: "none",
              "&:focus": {
                borderColor: BLUE,
                boxShadow:
                  "0 0 0 3px rgba(30, 58, 95, 0.12), 0 4px 14px rgba(30, 58, 95, 0.18)",
              },
              "&::placeholder": {
                color: DARK_NAVY_LIGHT,
                opacity: 0.7,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mt: 2,
            }}
          >
            <LuShield size={16} color={DARK_NAVY_LIGHT} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: DARK_NAVY_LIGHT, lineHeight: 1.5 }}>
              Your changes will be sent to your RM for verification. Existing
              details will remain active until approval.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderTop: "1px solid #F0F0F0",
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={handleClose}
            sx={{
              px: 2.5,
              py: 1.25,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: PROFILE_FONT,
              color: DARK_NAVY,
              bgcolor: "#ffffff",
              border: "1px solid #D0D0D0",
              borderRadius: "10px",
              cursor: "pointer",
              "&:hover": { bgcolor: "#F5F5F5" },
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleConfirm}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 1.25,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: PROFILE_FONT,
              color: "#ffffff",
              bgcolor: BLUE,
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              "&:hover": { bgcolor: BTN_HOVER },
            }}
          >
            Submit for Verification
            <LuArrowRight size={16} />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditModal;
